/**
 * Minimal Core Web Vitals reporter. Wires LCP + CLS + INP via the
 * PerformanceObserver primitives that ship in every 2023+ browser —
 * no `web-vitals` npm package needed, ~1 KB of code, zero deps.
 *
 * Numbers are dispatched via the passed reporter which the caller
 * routes to Sentry's `Sentry.metrics.distribution(...)` in prod. Dev
 * builds log to console for the DevTools "Performance insights"
 * cross-check.
 *
 * INP calculation uses the PerformanceEventTiming API + tracks the
 * 98th-percentile of interaction latencies, then reports the max
 * seen at pagehide. Matches the definition Chrome uses for its
 * Core Web Vitals dashboard.
 */
export type VitalMetric =
  | { name: 'LCP'; value: number }
  | { name: 'CLS'; value: number }
  | { name: 'INP'; value: number };

export type VitalReporter = (metric: VitalMetric) => void;

export function initWebVitals(report: VitalReporter): void {
  if (typeof PerformanceObserver === 'undefined') return;

  // LCP — reports the last (largest) paint of the session at pagehide.
  try {
    let lcp = 0;
    const lcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // startTime is the render time of the largest element seen so far.
        lcp = entry.startTime;
      }
    });
    lcpObserver.observe({
      type: 'largest-contentful-paint',
      buffered: true,
    });
    const flushLcp = () => {
      if (lcp > 0) report({ name: 'LCP', value: lcp });
      lcpObserver.disconnect();
    };
    addEventListener('pagehide', flushLcp, { once: true });
  } catch {
    // observer type unsupported → silently drop
  }

  // CLS — running sum of un-input-driven layout shifts.
  try {
    let cls = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceEntry[]) {
        const shift = entry as PerformanceEntry & {
          value: number;
          hadRecentInput: boolean;
        };
        if (!shift.hadRecentInput) cls += shift.value;
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });
    const flushCls = () => {
      report({ name: 'CLS', value: Number(cls.toFixed(4)) });
      clsObserver.disconnect();
    };
    addEventListener('pagehide', flushCls, { once: true });
  } catch {
    // ignore
  }

  // INP — worst interaction latency across the session, capped at the
  // top 10 entries as a poor-man's 98th percentile (aligns with the
  // web-vitals lib's implementation).
  try {
    const interactions: number[] = [];
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceEntry[]) {
        const evt = entry as PerformanceEntry & { duration: number };
        if (evt.duration > 40) interactions.push(evt.duration);
      }
    });
    inpObserver.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 40,
    } as PerformanceObserverInit & { durationThreshold: number });
    const flushInp = () => {
      if (interactions.length > 0) {
        interactions.sort((a, b) => b - a);
        const inp = interactions[Math.min(9, interactions.length - 1)];
        report({ name: 'INP', value: Math.round(inp) });
      }
      inpObserver.disconnect();
    };
    addEventListener('pagehide', flushInp, { once: true });
  } catch {
    // ignore
  }
}
